import React, { useState } from 'react';
import {
  FileText,
  Pencil,
  Trash2,
  Loader2,
  Check,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Download,
  BarChart3,
} from 'lucide-react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { adminAssessmentQuestions, scoreLabels } from '../data/adminConstants';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { assessmentsAPI, pdfAPI } from '../services/api';

const QuickscanDetailModal = ({
  assessment,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  onUpdateLeadStatus,
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [editData, setEditData] = useState({});
  const [showQualification, setShowQualification] = useState(false);
  const [detailCategory, setDetailCategory] = useState(null);

  // Start editing
  const handleStartEdit = () => {
    if (assessment) {
      setEditData({
        company: assessment.company || '',
        name: assessment.name || '',
        email: assessment.email || '',
        phone: assessment.phone || '',
        industry: assessment.industry || '',
        job_title: assessment.job_title || '',
        company_size: assessment.company_size || '',
        notes: assessment.notes || '',
      });
      setEditing(true);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditing(false);
    setEditData({});
  };

  // Save changes
  const handleSave = async () => {
    if (!assessment) return;
    
    setSaving(true);
    try {
      const updatedAssessment = await assessmentsAPI.update(assessment.id, editData);
      onUpdate(updatedAssessment);
      setEditing(false);
      setEditData({});
    } catch (error) {
      console.error('Failed to update assessment:', error);
      alert('Fout bij het opslaan van wijzigingen');
    } finally {
      setSaving(false);
    }
  };

  // Delete assessment
  const handleDelete = () => {
    if (window.confirm('Weet je zeker dat je deze quickscan wilt verwijderen? Dit kan niet ongedaan worden gemaakt.')) {
      onDelete(assessment.id);
    }
  };

  // Close modal and reset state
  const handleClose = () => {
    setEditing(false);
    setEditData({});
    setShowQualification(false);
    setDetailCategory(null);
    onClose();
  };

  // Get score color based on value
  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (!assessment) return null;

  // Spider diagram data (same 1-6 scale conversion as the comparison view)
  const dimensionTitleMap = {
    strategy: ['Strategie', 'strategy', 'Adoption'],
    governance: ['Governance', 'governance', 'Processes'],
    technology: ['Technologie & Data', 'technology', 'Data & Governance'],
    people: ['Mensen & Change', 'people', 'Skills & Change'],
  };
  const getCategoryPct = (catId) => {
    const names = dimensionTitleMap[catId] || [];
    const ds = (assessment.dimension_scores || []).find((d) => names.includes(d.dimension));
    return ds?.score ?? 0;
  };
  const radarData = Object.entries(adminAssessmentQuestions).map(([catId, cat]) => ({
    dimension: cat.title,
    score: Math.round((getCategoryPct(catId) / 100) * 6 * 10) / 10,
  }));
  const getAnswer = (qid) => {
    const a = (assessment.answers || []).find((x) => x.question_id === qid);
    if (!a) return null;
    if (a.not_applicable) return 'N.v.t.';
    return a.value;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-400" />
              Quickscan Details
              {editing && (
                <span className="ml-2 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                  Bewerken
                </span>
              )}
            </div>
            {!editing && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!assessment?.id) return;
                    setDownloadingPdf(true);
                    try {
                      const blob = await pdfAPI.generateFromAssessment(assessment.id);
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      const safeCompany = (assessment.company || 'quickscan').replace(/[^a-z0-9-_]+/gi, '_').slice(0, 40);
                      const safeName = (assessment.name || 'rapport').replace(/[^a-z0-9-_]+/gi, '_').slice(0, 40);
                      a.download = `quickscan_${safeCompany}_${safeName}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      console.error('PDF download failed:', err);
                      alert('Kon de PDF niet downloaden. Probeer het opnieuw.');
                    } finally {
                      setDownloadingPdf(false);
                    }
                  }}
                  disabled={downloadingPdf}
                  className="border-green-500 text-green-400 hover:bg-green-500/20"
                  data-testid="quickscan-download-pdf-btn"
                >
                  {downloadingPdf ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-1" />
                  )}
                  PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStartEdit}
                  className="border-blue-500 text-blue-400 hover:bg-blue-500/20"
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Bewerken
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  className="border-red-500 text-red-400 hover:bg-red-500/20"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Verwijderen
                </Button>
              </div>
            )}
          </DialogTitle>
          <DialogDescription className="text-slate-500 dark:text-slate-500 dark:text-slate-400">
            {editing ? 'Pas de gegevens aan en klik op Opslaan' : 'Bekijk alle gegevens van deze quickscan inclusief sales kwalificatie'}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {/* Contact Info - Editable or View Mode */}
          {editing ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs uppercase">Bedrijf</label>
                <Input
                  value={editData.company}
                  onChange={(e) => setEditData({ ...editData, company: e.target.value })}
                  className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                />
              </div>
              <div className="space-y-2">
                <label className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs uppercase">Industrie</label>
                <Input
                  value={editData.industry}
                  onChange={(e) => setEditData({ ...editData, industry: e.target.value })}
                  className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                />
              </div>
              <div className="space-y-2">
                <label className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs uppercase">Naam</label>
                <Input
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                />
              </div>
              <div className="space-y-2">
                <label className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs uppercase">Email</label>
                <Input
                  value={editData.email}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                />
              </div>
              <div className="space-y-2">
                <label className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs uppercase">Telefoon</label>
                <Input
                  value={editData.phone}
                  onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                  className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                  placeholder="+31 6 12345678"
                />
              </div>
              <div className="space-y-2">
                <label className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs uppercase">Functie</label>
                <Input
                  value={editData.job_title}
                  onChange={(e) => setEditData({ ...editData, job_title: e.target.value })}
                  className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                />
              </div>
              <div className="space-y-2">
                <label className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs uppercase">Bedrijfsgrootte</label>
                <Input
                  value={editData.company_size}
                  onChange={(e) => setEditData({ ...editData, company_size: e.target.value })}
                  className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                  placeholder="bijv. 50-100"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <label className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs uppercase">Notities</label>
                <textarea
                  value={editData.notes}
                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  className="w-full bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white rounded-lg p-3 min-h-[80px]"
                  placeholder="Voeg notities toe over deze lead..."
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-100 dark:bg-slate-700/50 p-4 rounded-lg">
                <p className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs uppercase mb-1">Bedrijf</p>
                <p className="text-slate-900 dark:text-white font-medium">{assessment.company}</p>
              </div>
              <div className="bg-slate-100 dark:bg-slate-700/50 p-4 rounded-lg">
                <p className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs uppercase mb-1">Industrie</p>
                <p className="text-slate-900 dark:text-white font-medium">{assessment.industry || '-'}</p>
              </div>
              <div className="bg-slate-100 dark:bg-slate-700/50 p-4 rounded-lg">
                <p className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs uppercase mb-1">Contact</p>
                <p className="text-slate-900 dark:text-white font-medium">{assessment.name}</p>
                <p className="text-blue-400 text-sm">{assessment.email}</p>
              </div>
              <div className="bg-slate-100 dark:bg-slate-700/50 p-4 rounded-lg">
                <p className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs uppercase mb-1">Telefoon</p>
                <p className="text-slate-900 dark:text-white font-medium">{assessment.phone || '-'}</p>
              </div>
              <div className="bg-slate-100 dark:bg-slate-700/50 p-4 rounded-lg">
                <p className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs uppercase mb-1">Functie</p>
                <p className="text-slate-900 dark:text-white font-medium">{assessment.job_title || '-'}</p>
              </div>
              <div className="bg-slate-100 dark:bg-slate-700/50 p-4 rounded-lg">
                <p className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs uppercase mb-1">Bedrijfsgrootte</p>
                <p className="text-slate-900 dark:text-white font-medium">{assessment.company_size || '-'} medewerkers</p>
              </div>
              {assessment.notes && (
                <div className="col-span-2 bg-slate-100 dark:bg-slate-700/50 p-4 rounded-lg">
                  <p className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs uppercase mb-1">Notities</p>
                  <p className="text-slate-900 dark:text-white">{assessment.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Edit mode action buttons */}
          {editing && (
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
              <Button
                variant="outline"
                onClick={handleCancelEdit}
                className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                Annuleren
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-slate-900 dark:text-white"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Opslaan...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Opslaan
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Sales Qualification Section - collapsed by default */}
          {!editing && (
            <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowQualification(!showQualification)}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-purple-500/10 transition-colors"
                data-testid="quickscan-qualification-toggle"
              >
                <p className="text-purple-400 text-xs uppercase flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Sales Kwalificatie (MAN)
                </p>
                {showQualification ? (
                  <ChevronUp className="h-4 w-4 text-purple-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-purple-400" />
                )}
              </button>
              {showQualification && (
              <div className="grid grid-cols-2 gap-4 p-4 pt-0">
                {/* Decision Maker - question 25 (Authority) */}
                <div className="bg-white dark:bg-slate-800/50 p-3 rounded-lg">
                  <p className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs mb-1">Besluitvormer (Authority)</p>
                  <p className="text-slate-900 dark:text-white font-medium text-sm">
                    {(() => {
                      const val = assessment.qualification_answers?.['25'] ?? assessment.qualification_answers?.['23'];
                      const map = {
                        me: 'Ik beslis zelfstandig',
                        team: 'HR-leiderschapsteam',
                        it: 'Samen met IT',
                        executive: 'Directie/bestuursniveau',
                        other: 'Anders',
                        '10': 'Beslist zelfstandig',
                        '11': 'Heeft goedkeuring nodig',
                      };
                      return map[String(val)] || val || '-';
                    })()}
                  </p>
                </div>
                {/* Need - question 26 (multiselect) */}
                <div className="bg-white dark:bg-slate-800/50 p-3 rounded-lg">
                  <p className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs mb-1">Grootste Uitdaging (Need)</p>
                  <p className="text-slate-900 dark:text-white font-medium text-sm">
                    {(() => {
                      const val = assessment.qualification_answers?.['26'] ?? assessment.qualification_answers?.['24'];
                      const labels = {
                        awareness: 'Bewustzijn en draagvlak creëren',
                        usecases: 'De juiste use cases identificeren',
                        implementation: 'AI-oplossingen implementeren',
                        skills: 'AI-vaardigheden opbouwen',
                        governance: 'Governance en beleid opzetten',
                        budget: 'Budget veiligstellen',
                        other: 'Anders',
                      };
                      if (Array.isArray(val)) {
                        if (val.length === 0) return '-';
                        return val.map((v) => labels[v] || v).join(', ');
                      }
                      if (typeof val === 'string' && val) return labels[val] || val;
                      return '-';
                    })()}
                    {assessment.qualification_answers?.['26_other_text'] && (
                      <span className="text-slate-500 dark:text-slate-500 dark:text-slate-400 italic"> — "{assessment.qualification_answers['26_other_text']}"</span>
                    )}
                  </p>
                </div>
                {/* Timeline - question 27 */}
                <div className="bg-white dark:bg-slate-800/50 p-3 rounded-lg">
                  <p className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs mb-1">Tijdsbestek AI Uitrol</p>
                  <p className="text-slate-900 dark:text-white font-medium text-sm">
                    {(() => {
                      const val = assessment.qualification_answers?.['27'] ?? assessment.qualification_answers?.['25'];
                      const map = {
                        '1': 'Binnen de maand',
                        '3': 'Binnen 3 maanden',
                        '6': 'Binnen 6 maanden',
                        '12': 'Binnen 12 maanden',
                        '18': 'Binnen 18 maanden',
                        'unknown': 'Geen idee',
                        '14': 'Al begonnen',
                        '13': 'Binnen 6 maanden',
                      };
                      return map[String(val)] || val || '-';
                    })()}
                  </p>
                </div>
                {/* Interest - question 28 */}
                <div className="bg-white dark:bg-slate-800/50 p-3 rounded-lg">
                  <p className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs mb-1">Interesse in Gesprek</p>
                  <p className="text-slate-900 dark:text-white font-medium text-sm">
                    {(() => {
                      const val = assessment.qualification_answers?.['28'] ??
                                  assessment.qualification_answers?.['27'] ??
                                  assessment.qualification_answers?.['26'];
                      const map = {
                        very: '🔥 Zeer geïnteresseerd - neem contact op',
                        somewhat: '📧 Enigszins geïnteresseerd - stuur info',
                        later: '⏰ Niet nu, misschien later',
                        no: '❄️ Nee, alleen resultaten',
                        '15': '🔥 Zeer geïnteresseerd',
                        '16': '📧 Stuur informatie',
                        '17': '❌ Geen interesse',
                      };
                      return map[String(val)] || '-';
                    })()}
                  </p>
                </div>
                {/* Lead Status */}
                <div className="col-span-2 bg-white dark:bg-slate-800/50 p-3 rounded-lg">
                  <p className="text-slate-500 dark:text-slate-500 dark:text-slate-400 text-xs mb-2">Lead Status (Aanpasbaar):</p>
                  <Select
                    value={assessment.lead_status || ''}
                    onValueChange={(value) => onUpdateLeadStatus(assessment.id, value)}
                  >
                    <SelectTrigger className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white w-48">
                      <SelectValue placeholder="Selecteer status" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                      <SelectItem value="hot" className="text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700">🔥 Hot Lead</SelectItem>
                      <SelectItem value="warm" className="text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700">🌡️ Warm</SelectItem>
                      <SelectItem value="nurture" className="text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700">🌱 Nurture</SelectItem>
                      <SelectItem value="info" className="text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700">📧 Info Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              )}
            </div>
          )}

          {/* Spider Diagram - Categorie Overzicht (zoals in vergelijkingsweergave) */}
          {!editing && (
            <div className="bg-slate-100 dark:bg-slate-700/50 p-4 rounded-lg" data-testid="quickscan-spider-section">
              <div className="flex justify-between items-center mb-3">
                <p className="text-slate-500 dark:text-slate-400 text-xs uppercase flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-purple-400" />
                  Spider Diagram - Categorie Overzicht
                </p>
                <div className="flex items-center gap-2">
                  <span className={`text-2xl font-bold ${getScoreColor(assessment.overall_score)}`}>
                    {assessment.overall_score}%
                  </span>
                  <span className="text-slate-500 dark:text-slate-400 text-sm">Overall</span>
                </div>
              </div>
              <div className="h-80" data-testid="quickscan-spider-chart">
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                  <RadarChart data={radarData} margin={{ top: 15, right: 30, bottom: 15, left: 30 }}>
                    <PolarGrid stroke="#475569" />
                    <PolarAngleAxis dataKey="dimension" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                    <PolarRadiusAxis
                      angle={90}
                      domain={[0, 6]}
                      tick={{ fill: '#64748b', fontSize: 10 }}
                      tickCount={7}
                    />
                    <Radar
                      name={assessment.company || 'Quickscan'}
                      dataKey="score"
                      stroke="#2563eb"
                      fill="#2563eb"
                      fillOpacity={0.2}
                      strokeWidth={2}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: '10px' }}
                      formatter={(value) => <span className="text-slate-700 dark:text-slate-300">{value}</span>}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#1e293b',
                        border: '1px solid #475569',
                        borderRadius: '8px',
                      }}
                      labelStyle={{ color: '#f8fafc' }}
                      itemStyle={{ color: '#94a3b8' }}
                      formatter={(value) => [`${value}/6`, '']}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-slate-500 dark:text-slate-500 text-xs mt-2 text-center">
                Schaal: 1-6 (Helemaal niet → Helemaal)
              </p>

              {/* Category drill-down buttons */}
              <div className="mt-4 pt-4 border-t border-slate-300 dark:border-slate-600">
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-3">
                  Klik op een categorie voor gedetailleerde vraag-scores:
                </p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                  {Object.entries(adminAssessmentQuestions).map(([catId, cat]) => (
                    <button
                      key={catId}
                      onClick={() => setDetailCategory(detailCategory === catId ? null : catId)}
                      data-testid={`quickscan-category-btn-${catId}`}
                      className={`px-4 py-2 rounded-lg text-sm transition-all flex items-center justify-center gap-2 border ${
                        detailCategory === catId
                          ? 'bg-purple-500/20 border-purple-500 text-purple-600 dark:text-purple-300'
                          : 'bg-white dark:bg-slate-700/50 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white hover:bg-purple-500/10 hover:border-purple-500'
                      }`}
                    >
                      {detailCategory === catId ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                      {cat.title}
                    </button>
                  ))}
                </div>
              </div>

              {/* Question detail for selected category */}
              {detailCategory && (
                <div className="mt-4 bg-white dark:bg-slate-800/50 rounded-lg p-4" data-testid="quickscan-category-detail">
                  <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                    <h4 className="text-slate-900 dark:text-white font-medium">
                      {adminAssessmentQuestions[detailCategory]?.title}{' '}
                      <span className="text-slate-500 dark:text-slate-400 font-normal text-sm">vraag-detail</span>
                    </h4>
                    <span className={`font-bold text-lg ${getScoreColor(getCategoryPct(detailCategory))}`}>
                      {getCategoryPct(detailCategory)}%
                    </span>
                  </div>
                  <div className="space-y-3">
                    {adminAssessmentQuestions[detailCategory]?.questions.map((question, qIdx) => {
                      const value = getAnswer(question.id);
                      const isNA = value === 'N.v.t.';
                      const numValue = typeof value === 'number' ? value : null;
                      const widthPercent = numValue ? (numValue / 6) * 100 : 0;
                      return (
                        <div key={question.id} className="space-y-1.5">
                          <p className="text-slate-600 dark:text-slate-300 text-sm">
                            <span className="text-slate-400 dark:text-slate-500 font-mono mr-2">{qIdx + 1}.</span>
                            {question.text}
                          </p>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-6 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden relative">
                              <div
                                className="h-full rounded-full transition-all duration-500 bg-blue-600"
                                style={{ width: `${widthPercent}%` }}
                              />
                              <span className="absolute inset-0 flex items-center justify-center text-xs text-slate-900 dark:text-white font-medium">
                                {isNA ? 'N.v.t.' : numValue ? `${numValue}/6` : '-'}
                              </span>
                            </div>
                            <span className="text-xs text-slate-500 dark:text-slate-400 w-32 text-right">
                              {numValue ? scoreLabels[numValue] : isNA ? 'Niet van toepassing' : '-'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Metadata */}
          {!editing && (
            <div className="text-slate-500 dark:text-slate-500 text-xs text-center pt-2 border-t border-slate-200 dark:border-slate-700">
              Ingevuld op {new Date(assessment.created_at).toLocaleString('nl-NL')}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickscanDetailModal;
